import type { SessionContext } from "@/platform";
import type { AccountRole } from "@/server/types";

import type { ShellPresentationProfile } from "./workspace-types";

/**
 * Presentation role for a `member` account, derived from its highest-priority
 * responsibility. Checked in order: lead, then builder, then reviewer, then
 * approver, then viewer/reader.
 */
function presentationRoleForMember(
  hasResponsibility: (responsibility: string) => boolean,
): AccountRole {
  if (hasResponsibility("lead")) {
    return "programme-lead";
  }
  if (hasResponsibility("builder")) {
    return "builder";
  }
  if (hasResponsibility("reviewer")) {
    return "reviewer";
  }
  if (hasResponsibility("approver")) {
    return "approver";
  }
  if (hasResponsibility("viewer") || hasResponsibility("reader")) {
    return "viewer";
  }
  return "member";
}

export function profileForPlatformSession(
  session: SessionContext | null,
  fallbackProfileId: string,
): ShellPresentationProfile {
  // Defence in depth for the accessor below. `PlatformSessionProvider` now
  // rejects a subjectless session so this function is only reached with a real
  // one, but it accepts `SessionContext | null` and this runs on every render
  // before the shell's error guard returns: reading `session.subject.accountRole`
  // off a present-but-subjectless session is exactly the crash this prevents.
  if (!session?.subject) {
    return {
      id: fallbackProfileId,
      label: fallbackProfileId,
      role: "deactivated",
      email: "",
      description: "No active platform session.",
    };
  }

  const hasResponsibility = (responsibility: string): boolean =>
    session.subject.responsibilities.some(
      (assignment) => assignment.responsibility === responsibility,
    );
  const presentationRole: AccountRole =
    session.subject.accountRole !== "member"
      ? session.subject.accountRole
      : presentationRoleForMember(hasResponsibility);

  return {
    id: session.user.id,
    label: session.user.displayName ?? session.user.email,
    role: presentationRole,
    email: session.user.email,
    description: `Signed in with a ${session.subject.accountRole} account.`,
  };
}
